import { showDefaultToast } from '@/helpers/toast-models'
import { useEventBus } from '@/plugins/eventBus/eventBus'
import { computed, ref, type ComputedRef } from 'vue'
import { fetchAddTagToShipping } from '../query'
import { findReceiptByBarCode } from './utils'

export interface CarrierTag {
  id: string
  barCode1: string
  barCode2: string | null
  productUnitId: string
}

export interface Receipt {
  id: string
  barCode1: string
  barCode2: string
  grouping: boolean
  shippingReceiptCarrierTags: CarrierTag[]
}

export function useBarCodeReader(
  receipts: ComputedRef<Receipt[]>,
  shippingId: string,
  emit: (event: 'update-receipts', ...args: any[]) => void
) {
  const eventBus = useEventBus()
  const selectedShippingReceipt = ref<Receipt | null>(null)
  const tagToAdd = ref<string | null>(null)
  const carrierTagId = ref<string | null>(null)
  const selectedProductId = ref<string | null>(null)
  const canScanTag = ref(false)
  const isAddTagToShippingReceiptFetching = ref(false)

  const isReceiptGrouping = computed(() => selectedShippingReceipt.value?.grouping)

  const { refetch: addTagToShippingReceipt, isError: isErrorOnAddTagToShippingReceipt } =
    fetchAddTagToShipping(tagToAdd, carrierTagId, shippingId, selectedShippingReceipt)

  const onScanShippingReceipt = (barCode: string, receipt?: Receipt) => {
    if (!receipt) {
      receipt = findReceiptByBarCode(barCode, receipts.value)!
    }

    selectedShippingReceipt.value = receipt

    const carrierTag = receipt.shippingReceiptCarrierTags.find((carrierTag) =>
      [carrierTag.barCode1, carrierTag.barCode2].includes(barCode)
    )

    carrierTagId.value = carrierTag?.id ?? null

    if (!isReceiptGrouping.value) {
      selectedProductId.value = carrierTag?.productUnitId ?? null
    }

    canScanTag.value = true
  }

  const onScanShippingReceiptTag = async (barCode: string) => {
    if (isAddTagToShippingReceiptFetching.value) return
    isAddTagToShippingReceiptFetching.value = true

    tagToAdd.value = barCode

    if (isReceiptGrouping.value) {
      carrierTagId.value = null
    }

    try {
      await addTagToShippingReceipt()

      const success = !isErrorOnAddTagToShippingReceipt.value

      if (!success) return

      if (!isReceiptGrouping.value) {
        canScanTag.value = false
        selectedProductId.value = null
      }

      showDefaultToast('Produto apontado com sucesso.', 'success', 2000)

      emit('update-receipts')
    } finally {
      isAddTagToShippingReceiptFetching.value = false
    }
  }

  const handleSubmit = (barCode: string) => {
    eventBus.emit('clear')

    const receiptShipping = findReceiptByBarCode(barCode, receipts.value)

    if (receiptShipping) {
      if (receiptShipping.grouping) {
        showDefaultToast(
          'Essa etiqueta de transporte é agrupada. Para iniciar a conferência, clique no botão "Iniciar conferência" referente à nota fiscal.',
          'error',
          3000
        )

        return
      }

      onScanShippingReceipt(barCode, receiptShipping)
      return
    }

    if (canScanTag.value) {
      onScanShippingReceiptTag(barCode)
      return
    }

    showDefaultToast('É necessária a leitura de uma nova etiqueta de transporte.', 'error', 3000)
  }

  const startListening = () => {
    eventBus.on('submit', handleSubmit)
  }

  const stopListening = () => {
    eventBus.off('submit', handleSubmit)
  }

  return {
    selectedShippingReceipt,
    selectedProductId,
    onScanShippingReceipt,
    startListening,
    stopListening
  }
}
